<template>
  <v-expansion-panel id="aoi-panel">
    <v-expansion-panel-header class="aoiPanelHeader"
      ><div
        :class="{
          'error--text': Array.isArray(aois) && aois.length > 0 && !aoisValid,
        }"
      >
        <span v-if="optional">AOI (Optional)</span>
        <span v-else>AOI</span
        >{{
          Array.isArray(aois) && aois.length > 0
            ? `${aois.length > 1 ? "s" : ""} (${Number(
                aois.length
              ).toLocaleString()})`
            : ""
        }}
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content ref="aoiPanelContent" eager>
      <div>
        <div class="text-center text-primary mt-4">
          Draw an AOI (Area Of Interest),<br />
          <upload @shapefileUploaded="onShapefileUploaded" />
          a Shapefile/GeoJSON,<br />
          or
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="paste-button"
                small
                color="#006AE3"
                text
                @click="showGeoJSONModal"
                outlined
                v-bind="attrs"
                v-on="on"
                style="border-color: #006ae3"
                >Paste</v-btn
              > </template
            >Add AOI's to the map with GeoJSON, BBox or Well-Known strings
            <div class="mt-4">
              <upload-requirements />
            </div>
          </v-tooltip>
          GeoJSON/WKT/BBox.
        </div>
        <div class="text--secondary">
          <div class="mt-4">
            <v-list-group
              :value="aoiRulesOpen"
              @input="
                aoiRulesOpen =
                  $event /* workaround for this issue: https://github.com/vuetifyjs/vuetify/issues/8166 */
              "
              class="aoiRequirementsGroup"
              dense
              color="rgba(0, 0, 0, 0.54)"
            >
              <template v-slot:activator>
                <v-list-item-title class="text--secondary"
                  >* Requirements</v-list-item-title
                >
              </template>

              <v-list-item style="min-height: auto">
                <span class="text-smaller text--secondary"
                  >AOI's must</span
                ></v-list-item
              >
              <v-list-item>
                <ul class="mb-2 text--secondary">
                  <li v-if="stack_depth">
                    total
                    {{ Number(aoiLimit).toLocaleString() }}
                    km² or less in area (stack depth:
                    {{ Number(stack_depth).toLocaleString() }})
                  </li>
                  <li>
                    total
                    {{ Number(limits.MAX_VERTEX_COUNT).toLocaleString() }}
                    vertices or less
                  </li>
                  <li>
                    total
                    {{ Number(limits.MAX_AOI_STRING_LENGTH).toLocaleString() }}
                    characters or less
                  </li>
                  <li
                    :class="{
                      'error--text':
                        Array.isArray(aois) &&
                        aois.length > 0 &&
                        numSmallAois > 0,
                    }"
                  >
                    be at least
                    {{ Number(limits.MIN_POLY_SIZE_SQ_KM).toLocaleString() }}
                    km² in area{{
                      numSmallAois > 0
                        ? ` (found ${Number(
                            numSmallAois
                          ).toLocaleString()} that ${
                            numSmallAois === 1 ? "is" : "are"
                          } too small)`
                        : ""
                    }}
                    <v-btn
                      v-if="numSmallAois > 0"
                      @click="removeSmallAois"
                      color="#006AE3"
                      title="Remove small AOI's"
                      text
                      small
                      ><v-icon small class="mr-1">mdi-close</v-icon
                      >Remove</v-btn
                    >
                  </li>
                  <li
                    :class="{
                      'error--text':
                        Array.isArray(aois) &&
                        aois.length > 0 &&
                        numIntersectedAois > 0,
                    }"
                  >
                    not have any self intersections{{
                      numIntersectedAois > 0
                        ? ` (found ${Number(
                            numIntersectedAois
                          ).toLocaleString()} that ${
                            numIntersectedAois === 1 ? "has" : "have"
                          } self intersections)`
                        : ""
                    }}
                    <v-btn
                      v-if="numIntersectedAois > 0"
                      @click="removeIntersectingAois"
                      color="#006AE3"
                      title="Remove AOI's with self intersections"
                      text
                      small
                      ><v-icon small class="mr-1">mdi-close</v-icon
                      >Remove</v-btn
                    >
                  </li>
                </ul>
              </v-list-item>
              <v-list-item>
                <div class="mt-2 text--secondary">
                  <div
                    :class="{
                      'error--text': areaUsed > aoiLimit,
                    }"
                  >
                    Area km²: {{ Number(areaUsed).toLocaleString() }}
                  </div>
                  <div
                    :class="{
                      'error--text': vertexCount > limits.MAX_VERTEX_COUNT,
                    }"
                  >
                    # of vertices: {{ Number(vertexCount).toLocaleString() }}
                  </div>
                  <div
                    :class="{
                      'error--text': aoisLen > limits.MAX_AOI_STRING_LENGTH,
                    }"
                  >
                    # of characters: {{ Number(aoisLen).toLocaleString() }}
                  </div>
                </div>
              </v-list-item>
            </v-list-group>
          </div>
        </div>
      </div>
    </v-expansion-panel-content>
    <paste-geojson-wkt ref="geoJSONModal" @submit="onGeoJSONPasted" />
    <layers-to-add-dialog
      ref="addLayersDialog"
      :layers="layersToAdd"
      :fileName="fileName"
      @submit="processFeatureCollections"
      :limits="limits"
    />
    <feature-processor
      ref="processor"
      :featureCollections="featureCollectionsToProcess"
      :map="map"
      :limits="limits"
      @done="onProcessingDone"
    />
  </v-expansion-panel>
</template>

<script>
import PasteGeojsonWkt from "@/components/aoiPanel/PasteGeojsonWkt";
import LayersToAddDialog from "@/components/aoiPanel/LayersToAddDialog";
import FeatureProcessor from "@/components/aoiPanel/FeatureProcessor";
import Upload from "@/components/aoiPanel/Upload";
import UploadRequirements from "@/components/aoiPanel/UploadRequirements";
import mapUtils from "@/utils/map";
import { createNamespacedHelpers } from "vuex-composition-helpers";
const uiStore = createNamespacedHelpers("ui");

export default {
  mixins: [mapUtils],
  components: {
    Upload,
    PasteGeojsonWkt,
    UploadRequirements,
    LayersToAddDialog,
    FeatureProcessor,
  },
  props: {
    map: {
      type: Object,
      required: true,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    highlight: {
      type: Boolean,
      default: false,
    },
    stack_depth: Number,
  },
  watch: {
    aois: {
      immediate: false,
      deep: true,
      handler(newValue) {
        if (this.shouldShowRequirements(newValue)) {
          this.aoiRulesOpen = true;
        }
      },
    },
  },
  data() {
    return {
      aois: [],
      aoiRulesOpen: false,
      limits: {
        MAX_AOI_SIZE_SQ_KM: 100000,
        MAX_EXPLORE_AOI_SIZE_SQ_KM: 500000,
        MIN_AOI_SIZE_SQ_KM: 25,
        MIN_POLY_SIZE_SQ_KM: 1,
        MIN_POLY_WIDTH_METERS: 250,
        MAX_DIGIT_GEOMETRY_PRECISION: 8,
        MAX_VERTEX_COUNT: 1000,
        MAX_AOI_STRING_LENGTH: 250000,
        // MAX_AOI_STRING_LENGTH: 250000000,
        MIN_SUN_ELEVATION: 5,
        MAX_OFF_NADIR: 30,
      },
      sqkm_factor: 0.035,
      stack_depth_factor: 25,
      query_limit: 700,
      layersToAdd: [],
      fileName: null,
      featureCollectionsToProcess: [],
    };
  },
  computed: {
    areaUsed() {
      return this.calculateKmSquaredArea(this.aois);
    },
    vertexCount() {
      return this.getVertexCount(this.aois);
    },
    aoisLen() {
      return this.getFeaturesCharacterLength(this.aois);
    },
    aoisValid() {
      return (
        Array.isArray(this.aois) &&
        this.aois.length > 0 &&
        this.areaUsed < this.aoiLimit &&
        this.vertexCount < this.limits.MAX_VERTEX_COUNT &&
        this.aoisLen < this.limits.MAX_AOI_STRING_LENGTH &&
        !this.numIntersectedAois &&
        !this.numSmallAois
      );
    },
    numIntersectedAois() {
      return this.getIntersectedAoisCount(this.aois);
    },
    numSmallAois() {
      return this.getSmallAoisCount(this.aois, this.limits.MIN_POLY_SIZE_SQ_KM);
    },
    aoiLimit() {
      if (this.stack_depth) {
        // calculate aoi limit based on stack depth
        const stack_depth_value = this.stack_depth * this.stack_depth_factor;
        let sqkm_limit =
          (this.query_limit - stack_depth_value) / this.sqkm_factor;

        // set 1000 sqkm as a minimum limit
        if (sqkm_limit < 1000) {
          sqkm_limit = 1000;
        }

        return parseInt(sqkm_limit);
      } else {
        return this.limits.MAX_EXPLORE_AOI_SIZE_SQ_KM;
      }
    },
  },
  setup() {
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR };
  },
  mounted() {
    this.$emit("mounted");

    if (typeof this.$attrs.highlight !== undefined) {
      this.$nextTick(() => {
        const panel = this.$refs.aoiPanelContent.$el;
        this.$log.debug("highlighting aoi panel...");
        panel.classList.add("highlightPanel");

        setTimeout(() => {
          panel.classList.remove("highlightPanel");
        }, 5000);
      });
    }
  },
  methods: {
    shouldShowRequirements(aois) {
      return (
        Array.isArray(aois) &&
        aois.length > 0 &&
        (this.calculateKmSquaredArea(aois) > this.aoiLimit ||
          this.getVertexCount(aois) > this.limits.MAX_VERTEX_COUNT ||
          this.getFeaturesCharacterLength(aois) >
            this.limits.MAX_AOI_STRING_LENGTH ||
          this.getIntersectedAoisCount(aois) > 0 ||
          this.getSmallAoisCount(aois, this.limits.MIN_POLY_SIZE_SQ_KM) > 0)
      );
    },
    showGeoJSONModal() {
      this.$refs.geoJSONModal.show();
    },
    onGeoJSONPasted(geojson) {
      this.$log.debug("geojson pasted");
      // this.$log.debug(`geojson::${JSON.stringify(geojson)}`);

      try {
        if (
          geojson &&
          Array.isArray(geojson.features) &&
          geojson.features.length > 0
        ) {
          this.fileName = null;
          this.processFeatureCollections([geojson]);
        } else {
          this.SHOW_SNACKBAR({
            text: `Failed to add GeoJSON/WKT/BBox, it is an invalid format`,
            color: "error",
            icon: "mdi-alert-circle-outline",
          });
        }
      } catch (err) {
        this.SHOW_SNACKBAR({
          text: `Failed to add GeoJSON/WKT/BBox due to the following: ${err.toString()}`,
          color: "error",
          icon: "mdi-alert-circle-outline",
        });
      }
    },
    processFeatureCollections(featureCollections, fileName = null) {
      this.$log.debug(
        `processFeatureCollections(# of featureCollections=${featureCollections.length}, fileName=${fileName})`
      );
      this.featureCollectionsToProcess = [...featureCollections];
      this.fileName = fileName;

      setTimeout(() => {
        this.$refs.processor.processFeatureCollections();
      }, 50);
    },
    onProcessingDone(addedFeatures) {
      if (
        Array.isArray(addedFeatures) &&
        addedFeatures.length > 0 &&
        this.shouldShowRequirements(addedFeatures)
      ) {
        this.aoiRulesOpen = true;
      }
    },
    onShapefileUploaded(result, file) {
      this.$log.debug("shapefile uploaded", file);

      if (Array.isArray(result)) {
        this.fileName = file.name;
        this.layersToAdd = [...result];
        this.$refs.addLayersDialog.show();
      } else {
        if (result.features.length > 0) {
          this.processFeatureCollections([result], file.name);
        } else {
          this.SHOW_SNACKBAR({
            text: `Failed to add shapefile/geojson: ${file.name}.  It is not valid or a recognized data format.`,
            color: "error",
            timeout: -1,
            btnText: "Close",
            icon: "mdi-alert-circle-outline",
          });
        }
      }
    },
    removeSmallAois() {
      const smallAois = this.getSmallAois(
        this.aois,
        this.limits.MIN_POLY_SIZE_SQ_KM
      );

      smallAois.forEach((aoi) => {
        this.map.removeDrawnFeature(aoi.id);
      }, this);
    },
    removeIntersectingAois() {
      const intersectingAois = this.getIntersectedAois(this.aois);

      intersectingAois.forEach((aoi) => {
        this.map.removeDrawnFeature(aoi.id);
      }, this);
    },
  },
};
</script>

<style lang="scss" scoped>
.text-smaller {
  font-size: 0.8rem;
}
</style>

<style lang="scss">
.aoiRequirementsGroup .v-list-group__header.v-list-item {
  /* use negative margins to keep the padding with the button borders and text/icon */
  margin-left: -16px;
  margin-right: -16px;
}
.aoiRequirementsGroup
  .v-list-group__header.v-list-item
  .v-list-group__header__append-icon
  i.v-icon.v-icon {
  color: rgba(255, 255, 255, 0.7); /* text-secondary value */
}

@-webkit-keyframes highlightAnimation {
  0% {
    background-color: white;
    opacity: 1;
  }
  100% {
    background-color: #1e1e1e;
  }
}

.highlightPanel {
  //https://stackoverflow.com/questions/16791851/a-flash-of-color-using-pure-css-transitions
  -webkit-animation-name: highlightAnimation;
  -webkit-animation-duration: 1000ms;
  -webkit-animation-iteration-count: 1;
  -webkit-animation-timing-function: cubic-bezier(0.55, 0.085, 0.68, 0.53);
  -moz-animation-name: highlightAnimation;
  -moz-animation-duration: 1000ms;
  -moz-animation-iteration-count: 1;
  -moz-animation-timing-function: cubic-bezier(0.55, 0.085, 0.68, 0.53);
}
</style>
